import { Trans, useT } from '@eturi/react-i18next'
import type {
	DeviceInfoChildAppStatus,
	DeviceInfoOnlineStatus,
	DeviceInfoPairStatus,
	ScreenTimeViewData,
} from '@op/app-shared'
import cls from 'classnames'
import type { ReactNode } from 'react'
import { DateTime, EllipsisLoader, Glyph } from '../../widgets'
import { useStatusTextColor } from '../ChildStatus/useStatusColor'

type InfoRowProps = {
	readonly children: ReactNode
	readonly label: string
	readonly onInfoPress?: () => void
}

export const InfoRow = ({ children, label, onInfoPress }: InfoRowProps) => {
	return (
		<div
			className={cls(
				'flex items-center justify-between w-full rounded-xl px-4 h-12 bg-box',
				onInfoPress && 'cursor-pointer',
			)}
			onClick={onInfoPress}
		>
			<div className="flex items-center gap-2">
				<p className="font-bold">{label}</p>

				{!!onInfoPress && <Glyph name="i-info" className="text-md text-teal" />}
			</div>

			{children}
		</div>
	)
}

type DeviceBatteryLifeProps = {
	readonly battery: number | null
}

export const DeviceBatteryLife = ({ battery }: DeviceBatteryLifeProps) => {
	const [t] = useT('devices.device')

	return (
		<InfoRow label={t('battery_life')}>
			<p>{battery == null ? t('unknown') : `${battery}%`}</p>
		</InfoRow>
	)
}

type DeviceChildAppStatusProps = {
	readonly childApp: DeviceInfoChildAppStatus
	readonly isInstalling: boolean
	readonly onPress: () => void
}

export const DeviceChildAppStatus = ({
	childApp,
	isInstalling,
	onPress,
}: DeviceChildAppStatusProps) => {
	const [t] = useT()

	return (
		<InfoRow label={t('devices.device.ourpact_jr')} onInfoPress={onPress}>
			<EllipsisLoader loading={isInstalling}>
				{isInstalling ?
					t('devices.device.installing')
				:	t(childApp == 'installed' ? 'actions.installed' : 'devices.device.unknown')}
			</EllipsisLoader>
		</InfoRow>
	)
}

type DeviceOnlineStatusProps = {
	readonly status: DeviceInfoOnlineStatus
}

export const DeviceOnlineStatus = ({ status }: DeviceOnlineStatusProps) => {
	const [t] = useT('devices.device')

	return (
		<InfoRow label={t('phone_activity')}>
			<p>{t(status)}</p>
		</InfoRow>
	)
}

type DevicePairDateProps = {
	readonly pairTs: number
}

export const DevicePairDate = ({ pairTs }: DevicePairDateProps) => {
	const [t] = useT()

	return (
		<InfoRow label={t('devices.device.paired_date')}>
			<DateTime date={pairTs} format="LLL" />
		</InfoRow>
	)
}

type DevicePairStatusProps = {
	readonly onPress?: () => void
	readonly pairStatus: DeviceInfoPairStatus
}

export const DevicePairStatus = ({ onPress, pairStatus }: DevicePairStatusProps) => {
	const [t] = useT('devices.device')

	const content = (() => {
		if (pairStatus == 'full_pair') return <p>{t('status.full_paired')}</p>
		if (pairStatus == 'quick_pair') return <p>{t('status.quick_paired')}</p>

		return (
			<div className="flex items-center gap-2">
				<Glyph name="warning" className="text-sm text-red" />
				<p className="text-red">{t('status.needs_pairing')}</p>
			</div>
		)
	})()

	return (
		<InfoRow label={t('pair_status')} onInfoPress={onPress}>
			{content}
		</InfoRow>
	)
}

type DeviceTamperproofStatusProps = {
	readonly onPress?: () => void
	readonly tamperproofed: boolean | null
}

export const DeviceTamperproofStatus = ({
	onPress,
	tamperproofed,
}: DeviceTamperproofStatusProps) => {
	const [t] = useT()

	// Null means the device doesn't support tamperproofing, so don't show the row.
	if (tamperproofed == null) return null

	return (
		<InfoRow label={t('devices.device.tamperproof')} onInfoPress={onPress}>
			<p>{tamperproofed ? t('actions.enabled') : t('actions.not_enabled')}</p>
		</InfoRow>
	)
}

type ScreenTimeInfoRowProps = {
	readonly screenTime: ScreenTimeViewData
}

export const ScreenTimeInfoRow = ({ screenTime }: ScreenTimeInfoRowProps) => {
	const [t] = useT('devices.device')
	const color = useStatusTextColor(screenTime.colorState)

	if (screenTime.display == 'unmanaged') {
		return (
			<InfoRow label={t('screen_time')}>
				<p>{t('unable_to_block')}</p>
			</InfoRow>
		)
	}

	return (
		<InfoRow label={t('screen_time')}>
			<div className="flex items-center gap-2">
				<div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />

				<Trans
					i18nKey="block_statuses.access_blockedOrGranted"
					tOptions={{
						blockedOrGranted: t.$(`block_statuses.${screenTime.statusKey}`),
						color: undefined,
					}}
				/>
			</div>
		</InfoRow>
	)
}
